import React, { useState, useRef, useEffect } from 'react';
import { imagePlaceholderSrc } from '_utils/styles';
import { SimpleArrow } from '_utils/icons';
import { convertSrcJss, createSrcSet } from '_utils/media';
import { useTranslation } from 'react-i18next';

import {
  Container,
  ThumbsContainer,
  Thumbs,
  ThumbContainer,
  ThumbImage,
  Slides,
  Carousel,
  Slide,
  ImageContainer,
  SlideImage,
  CaptionContainer,
  SlideCount,
  Caption,
  PrevNextPosition,
  PrevNextButton,
  Title,
  Credit,
  ArrowWrapper
} from './StyledModalGallery';
import { usePrevious } from '_utils/hooks';
import ScreenReader from '_utils/components/Corporate/ScreenReader';
import PropTypes from 'prop-types';

let isIE11 = false;

if (typeof window !== 'undefined') {
  require('flickity-as-nav-for');
  require('_utils/helpers/detectElementResize');
  isIE11 = !!(window as any).MSInputMethodContext && !!(document as any).documentMode;
}

const getRandomId = () =>
  Math.random()
    .toString()
    .substr(2);

const noop = () => {};

const ModalGallery = ({ gallery }) => {
  // filter by items that have data
  let items = gallery
    .filter(({ item }) => Boolean(item?.data?.value?.src))
    .map(({ item, caption, credit, title }) => ({
      item: item.data.value,
      caption: caption?.data,
      credit: credit?.data,
      title: title?.data
    }));
  const [thumbsId] = useState(`gallery-thumbs-${getRandomId()}`);
  const [slidesId] = useState(`gallery-slides-${getRandomId()}`);
  const [flickity, setFlickity] = useState(null);
  const [thumbsFlickity, setThumbsFlickity] = useState(null);
  const [thumbsExtendLeft, setThumbsExtendLeft] = useState(false);
  const [thumbsExtendRight, setThumbsExtendRight] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const [prevEnabled, setPrevEnabled] = useState(false);
  const [nextEnabled, setNextEnabled] = useState(false);
  const [slideIndex, setSlideIndex] = useState(0);
  const [t] = useTranslation();

  const containerRef = useRef();

  const prevFlickity = usePrevious(flickity);

  const scrollListenerRef = useRef((scroll) => {
    if (scroll > 0.01) {
      setThumbsExtendLeft(true);
    } else {
      setThumbsExtendLeft(false);
    }

    if (scroll < 0.99) {
      setThumbsExtendRight(true);
    } else {
      setThumbsExtendRight(false);
    }
  });

  // note 'static' that prevents breaking SSR
  const thumbOptions = {
    cellAlign: 'left',
    contain: true,
    draggable: true,
    pageDots: false,
    prevNextButtons: false,
    asNavFor: `.${slidesId}`,
    static: true,
    accessibility: false
  };

  const mainOptions = {
    contain: true,
    draggable: true,
    pageDots: false,
    prevNextButtons: false,
    setGallerySize: true,
    static: true,
    accessibility: false
  };

  // scroll listener on thumbs to add gradient fade
  useEffect(() => {
    const scrollListener = scrollListenerRef.current;

    if (thumbsFlickity) {
      thumbsFlickity.off('scroll', scrollListener);
      thumbsFlickity.on('scroll', scrollListener);
    }

    return () => {
      if (thumbsFlickity) {
        thumbsFlickity.off('scroll', scrollListener);
      }
    };
  }, [thumbsFlickity]);

  // because the modal container has scrolling auto, the container size can jump
  // during initialisation, causing flickity to calculate slide position/size
  // incorrectly.
  // Here we wait for initialisation of flickity and add a listener to container
  // size changes to force update
  useEffect(() => {
    const container: HTMLElement = containerRef.current;

    const addResizeListener =
      typeof window !== undefined && window['addResizeListener']
        ? window['addResizeListener']
        : noop;

    const removeResizeListener =
      typeof window !== undefined && window['removeResizeListener']
        ? window['removeResizeListener']
        : noop;

    if (!prevFlickity && flickity) {
      const resizeListener = () => {
        if (flickity && container.offsetWidth !== containerWidth) {
          setTimeout(() => {
            flickity.resize();
          }, 0);
          setContainerWidth(container.offsetWidth);
        }
      };

      addResizeListener(container, resizeListener);

      return () => {
        removeResizeListener(container, resizeListener);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flickity, items]);

  // listen to flickity select to update current slide index
  useEffect(() => {
    const selectListener = (i) => {
      setPrevEnabled(items && i > 0);
      setNextEnabled(items && i < items.length - 1);
      setSlideIndex(i);
    };

    if (!prevFlickity && flickity) {
      flickity.on('select', selectListener);

      return () => {
        flickity.off('select', selectListener);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flickity]);

  if (!items.length) {
    return null;
  }

  return (
    <Container ref={containerRef}>
      {items.length > 1 && (
        <ThumbsContainer aria-label="gallery">
          <Thumbs
            // TODO handle key presses when focused and fire selected thumb
            className={`${thumbsId} ${thumbsExtendLeft ? 'fade-left' : ''} ${
              thumbsExtendRight ? 'fade-right' : ''
            }`}
            options={thumbOptions}
            flickityRef={(flickity) => {
              setThumbsFlickity(flickity);
            }}
          >
            {items.map(({ item }, index) => (
              <ThumbContainer
                aria-label={`${t('gallery-accessible-open-slide-prefix')} ${index + 1}`}
                key={`thumb-${index}`}
                onClick={() => {
                  try {
                    flickity.select(index);
                    flickity.once('settle', (i) => {
                      flickity.getCellElements()[i].focus();
                    });
                  } catch (err) {}
                }}
              >
                <ThumbImage
                  className="lazyload"
                  draggable={false}
                  field={{
                    value: {
                      src: imagePlaceholderSrc,
                      alt: item.alt,
                      'data-src': convertSrcJss(item.src) + '?mw=200'
                    }
                  }}
                />
              </ThumbContainer>
            ))}
          </Thumbs>
        </ThumbsContainer>
      )}
      <Slides>
        <PrevNextPosition className="prev">
          <PrevNextButton
            onClick={() => {
              flickity.previous();
              try {
                flickity.once('settle', (i) => {
                  flickity.getCellElements()[i].focus();
                });
              } catch (err) {}
            }}
            disabled={!prevEnabled}
          >
            <ScreenReader>{t('gallery-accessible-previous-slide-label')}</ScreenReader>
            <ArrowWrapper>
              <SimpleArrow ariaHidden={true} />
            </ArrowWrapper>
          </PrevNextButton>
        </PrevNextPosition>
        <Carousel
          className={slidesId}
          options={mainOptions}
          disableImagesLoaded={true}
          flickityRef={(flickity) => {
            setFlickity(flickity);
          }}
        >
          {items.map(({ item, caption, credit, title }, index) => (
            <Slide key={`slide-${index}`} tabIndex={index === slideIndex ? 0 : -1}>
              <ImageContainer>
                <SlideImage
                  className="lazyload"
                  draggable={false}
                  field={{
                    value: {
                      src: isIE11 ? convertSrcJss(item.src) + '?mw=1200' : imagePlaceholderSrc,
                      alt: item.alt,
                      'data-src': convertSrcJss(item.src) + '?mw=1200',
                      'data-srcset': createSrcSet(item.src, [640, 1200, 1600])
                    }
                  }}
                />
              </ImageContainer>
              <CaptionContainer>
                <SlideCount role="text">
                  <ScreenReader>{t('gallery-accessible-current-slide-prefix')} </ScreenReader>
                  {index + 1}
                  <ScreenReader> {t('gallery-accessible-slide-total-prefix')} </ScreenReader>
                  <span aria-hidden={true}>/</span>
                  {items.length}
                </SlideCount>
                <div>
                  {title.value && <Title tag="p" field={title} />}
                  {caption.value && <Caption field={caption} />}
                  {credit.value && <Credit field={credit} tag="p" />}
                </div>
              </CaptionContainer>
            </Slide>
          ))}
        </Carousel>
        <PrevNextPosition className="next">
          <PrevNextButton
            onClick={() => {
              flickity.next();
              try {
                flickity.once('settle', (i) => {
                  flickity.getCellElements()[i].focus();
                });
              } catch (err) {}
            }}
            disabled={!nextEnabled}
          >
            <ScreenReader>{t('gallery-accessible-next-slide-label')}</ScreenReader>
            <ArrowWrapper>
              <SimpleArrow ariaHidden={true} />
            </ArrowWrapper>
          </PrevNextButton>
        </PrevNextPosition>
      </Slides>
    </Container>
  );
};

ModalGallery.propTypes = {
  gallery: PropTypes.arrayOf(
    PropTypes.shape({
      item: PropTypes.shape({
        data: PropTypes.shape({
          value: PropTypes.shape({
            src: PropTypes.string,
            alt: PropTypes.string,
          }),
        }),
      }),
      caption: PropTypes.shape({
        data: PropTypes.shape({
          value: PropTypes.string,
        }),
      }),
      credit: PropTypes.shape({
        data: PropTypes.shape({
          value: PropTypes.string,
        }),
      }),
      title: PropTypes.shape({
        data: PropTypes.shape({
          value: PropTypes.string,
        }),
      }),
    })
  ).isRequired,
};

export default ModalGallery;
